import { useEffect, useMemo, useState } from 'react';
import {
  operatorConsole,
  type InventoryParams,
  type InventoryResponse,
} from '../../../lib/api/operatorConsole';
import { toInventoryParams, type InventoryFilterValues } from './inventoryFilters';

export const INVENTORY_DEBOUNCE_MS = 300;
export const INVENTORY_PER_PAGE = 50;

export interface UseInventoryResult {
  data: InventoryResponse | null;
  loading: boolean;
  error: string | null;
}

function isAbort(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    'name' in err &&
    (err as { name: unknown }).name === 'AbortError'
  );
}

/**
 * Inventory search for the Items tab. Every filter/page change restarts a
 * 300 ms debounce; when it fires, the previous in-flight request is aborted so
 * a slow answer can never paint over a newer one. `version` forces a refetch
 * after add/remove (membership changed, the filters did not).
 *
 * `filters` does NOT have to be a stable object — the hook compares the request
 * params by value, so an equal filter set rebuilt on every render refetches
 * nothing.
 */
export function useInventory(
  packId: number,
  filters: InventoryFilterValues,
  page: number,
  version: number,
): UseInventoryResult {
  const [data, setData] = useState<InventoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Key the effect on the VALUE of the request params, not on the identity of
  // `filters`: a caller that rebuilds `filtersFromSearchParams(searchParams)`
  // inline gets a fresh object after every `setData`, which would otherwise
  // restart the debounce forever (fetch loop against the ES-backed endpoint).
  const key = JSON.stringify(toInventoryParams(filters, packId, page, INVENTORY_PER_PAGE));
  const params = useMemo(() => JSON.parse(key) as InventoryParams, [key]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    const timer = setTimeout(() => {
      operatorConsole.packBuilder
        .inventory(params, controller.signal)
        .then((res) => {
          if (cancelled) return;
          setData(res);
          setError(null);
          setLoading(false);
        })
        .catch((err: unknown) => {
          if (cancelled || isAbort(err)) return;
          setError(err instanceof Error ? err.message : 'Unknown error');
          setLoading(false);
        });
    }, INVENTORY_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [params, version]);

  return { data, loading, error };
}
